import React, {useEffect} from "react";
import {QueryClientProvider} from "@tanstack/react-query";

import App from "App";
import {EditsContextProvider} from "contexts/edits";
import {ImagesContextProvider} from "contexts/images";
import {SettingContextProvider} from "contexts/setting";
import {Navigate, useNavigate, useLocation, useSearchParams} from "react-router-dom";
import {ThemeProvider} from "@rescui/ui-contexts";
import StagingNotice from "layout/StagingNotice";
import {ViewAsRoleBar} from "layout/ViewAsRole";
import {ErrorDialogProvider} from "layout/ErrorDialog";
import {track} from "libs/analytics";
import {useAuth} from "hooks/useAuth";
import {setRequestEnvelopeUserEmail} from "libs/requestEnvelope";
import {
  lastLocationFor,
  pageHasUserAction,
  readLastLocation,
  saveLastLocation,
} from "libs/lastLocation";
import {URL_REWRITE_OF} from "hooks/useBackgroundNumbers";
import {queryClient} from "libs/queryClient";
import {isChannelPathHidden} from "utils/roles";
import {LiveTemplateSelectionGate} from "hooks/useTemplateSelection";

import "@rescui/colors/lib/index.css";

const theme = "dark";

// Inner component so useAuth (which uses useQuery) runs underneath
// QueryClientProvider, not outside it.
const AppRootInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isRenderMode = searchParams.get("render") === "true";
  const {user, role, featureFlags, isLoading} = useAuth();

  useEffect(() => {
    if (isRenderMode || isLoading) return;
    if (!user) {
      const returnTo = location.pathname + location.search;
      navigate(`/login?next=${encodeURIComponent(returnTo)}`);
    } else {
      track("neurobaker.session.started", {role});
    }
  }, [isLoading, user]);

  useEffect(() => {
    if (isRenderMode) return;
    track("neurobaker.page.viewed", {path: location.pathname});
  }, [location.pathname]);

  useEffect(() => {
    // Surfaced in error envelopes / copy-diagnostics dumps so support can
    // identify which user hit a given failure.
    setRequestEnvelopeUserEmail(user?.email || null);
  }, [user?.email]);

  // Each selector change writes the URL, so the URL is the selector position
  // the user leaves the editor with. The index route opens it again on the
  // next visit (libs/lastLocation.ts). A page that only opened a URL keeps
  // nothing, because the user changed no selector on it. The renderer is not
  // a user, so render mode keeps nothing.
  // A URL that the page rewrote (a background's new tile number,
  // hooks/useBackgroundNumbers.ts) is the same position: it replaces the kept
  // location only where that location was the URL before.
  useEffect(() => {
    if (isRenderMode || !user?.email || !pageHasUserAction()) return;
    const viewer = {role, featureFlags};
    const lastLocation = lastLocationFor(location.pathname, location.search, viewer);
    if (!lastLocation) return;
    const rewriteOf = location.state?.[URL_REWRITE_OF];
    if (
      typeof rewriteOf === "string" &&
      readLastLocation(user.email, viewer) !== lastLocationFor(location.pathname, rewriteOf, viewer)
    ) {
      return;
    }
    saveLastLocation(user.email, lastLocation);
  }, [
    isRenderMode,
    user?.email,
    role,
    featureFlags,
    location.pathname,
    location.search,
    location.state,
  ]);

  if (isLoading && !isRenderMode) {
    return null; // Don't render until auth is resolved
  }

  if (isChannelPathHidden(location.pathname, featureFlags)) {
    return <Navigate to="/" replace />;
  }

  return (
    <ThemeProvider theme={theme}>
      <EditsContextProvider>
        <ImagesContextProvider>
          <SettingContextProvider>
            <LiveTemplateSelectionGate>
              <App role={role} featureFlags={featureFlags} />
            </LiveTemplateSelectionGate>
            {!isRenderMode && <StagingNotice />}
            {!isRenderMode && <ViewAsRoleBar />}
            {!isRenderMode && <ErrorDialogProvider />}
          </SettingContextProvider>
        </ImagesContextProvider>
      </EditsContextProvider>
    </ThemeProvider>
  );
};

export const AppRoot = () => (
  <QueryClientProvider client={queryClient}>
    <AppRootInner />
  </QueryClientProvider>
);
